/**
 * Policy frontier — seeded Monte Carlo over the joint (choice × value) posterior.
 *
 * The estimand is gross/contribution value per ELIGIBLE PAYWALL USER under a
 * policy that assigns a menu to each user (plan §9bis.1 V4). All rows are
 * `scenario` claims: the evidence is observational (menu routing is confounded
 * by the condition tree), so no causal winner can be emitted (MVP criterion 9).
 */

import {
  validateOfferPurchaseContract,
  type FrontierInput,
  type FrontierResult,
  type FrontierRow,
  type MenuChoiceEvidence,
  type OfferValueSummary,
  type PolicySpec,
} from './contracts';
import { mulberry32, quantile, sampleDirichlet, type Rng } from './rng';
import { buildOfferValueModel, type OfferValueModel } from './survival';
import { hashInputBundle, normalizeNumber } from './replay';

export const MODEL_VERSION = 'pricing-offer-emulator/1.0.0';

interface MenuDraw {
  /** probability of choosing each offer of the menu (last cell = no conversion) */
  choiceProbs: Record<string, number>;
}

function validateInput(input: FrontierInput): string[] {
  const notes: string[] = [];
  const offerIds = new Set(input.offers.map((offer) => offer.offerId));
  for (const menu of input.menus) {
    for (const offerId of menu.offerIds) {
      if (!offerIds.has(offerId)) {
        throw new Error(`Menu ${menu.menuId} references unknown offer ${offerId}`);
      }
    }
  }
  for (const offer of input.offers) {
    const evidence = input.valueEvidence[offer.offerId];
    if (!evidence) {
      throw new Error(`Offer ${offer.offerId} has no value evidence`);
    }
    // Purchase-mode decision table (D4, C3-AC-001) — typed rejections only.
    validateOfferPurchaseContract(offer, evidence);
  }
  const policies = [input.baseline, ...input.candidatePolicies];
  for (const policy of policies) {
    for (const menuId of policyMenuIds(policy)) {
      if (!input.menus.some((menu) => menu.menuId === menuId)) {
        throw new Error(`Policy ${policy.policyId} references unknown menu ${menuId}`);
      }
    }
  }
  // D6 (C3-I2): every cancellation calibration in the bundle is recorded on
  // the run output — a declared prior is never an invisible constant.
  for (const [offerId, evidence] of Object.entries(input.valueEvidence)) {
    if (evidence.kind !== 'renewal_census' && evidence.kind !== 'weekly_renewal') continue;
    const calibration = evidence.pendingCancel?.calibration;
    if (!calibration) continue;
    notes.push(
      calibration.source === 'calibrated'
        ? `cancel_calibration:${offerId}:${calibration.calibrationVersion}`
        : `cancel_calibration_prior:${offerId}:${calibration.priorLabel}`,
    );
  }
  if (input.valueBasis === 'gross_audit') {
    notes.push(
      'value basis = gross (audit-labeled): contribution reconciliation not available — see plan §7.5 degrade policy',
    );
  }
  if (input.exposure.exposureMissing > 0) {
    notes.push(
      `exposure lane: ${input.exposure.exposureMissing} paywall-reached journeys without offer exposure — kept as a named lane, not imputed`,
    );
  }
  return notes;
}

function policyMenuIds(policy: PolicySpec): string[] {
  switch (policy.kind) {
    case 'current_mix':
      return Object.keys(policy.menuWeights);
    case 'global_menu':
      return [policy.menuId];
    case 'cohort':
      return policy.cohorts.map((cohort) => cohort.menuId);
    default: {
      const exhaustive: never = policy;
      throw new Error(`Unknown policy kind: ${JSON.stringify(exhaustive)}`);
    }
  }
}

function drawMenuChoices(rng: Rng, evidence: MenuChoiceEvidence, offerOrder: string[]): MenuDraw {
  const chosen = offerOrder.map((offerId) => evidence.choices[offerId] ?? 0);
  const chosenTotal = chosen.reduce((sum, count) => sum + count, 0);
  const none = Math.max(evidence.users - chosenTotal, 0);
  const probs = sampleDirichlet(rng, [...chosen, none]);
  const choiceProbs: Record<string, number> = {};
  offerOrder.forEach((offerId, index) => {
    choiceProbs[offerId] = probs[index];
  });
  return { choiceProbs };
}

function policyValue(
  policy: PolicySpec,
  menuValues: Record<string, number>,
): number {
  switch (policy.kind) {
    case 'current_mix': {
      const totalWeight = Object.values(policy.menuWeights).reduce((sum, weight) => sum + weight, 0);
      if (totalWeight <= 0) return 0;
      return Object.entries(policy.menuWeights).reduce(
        (sum, [menuId, weight]) => sum + (weight / totalWeight) * menuValues[menuId],
        0,
      );
    }
    case 'global_menu':
      return menuValues[policy.menuId];
    case 'cohort': {
      const totalShare = policy.cohorts.reduce((sum, cohort) => sum + cohort.share, 0);
      if (totalShare <= 0) return 0;
      return policy.cohorts.reduce(
        (sum, cohort) => sum + (cohort.share / totalShare) * menuValues[cohort.menuId],
        0,
      );
    }
    default: {
      const exhaustive: never = policy;
      throw new Error(`Unknown policy kind: ${JSON.stringify(exhaustive)}`);
    }
  }
}

function summarize(values: number[]): { p10: number; p50: number; p90: number; mean: number } {
  const sorted = [...values].sort((a, b) => a - b);
  const mean = sorted.reduce((sum, value) => sum + value, 0) / (sorted.length || 1);
  return {
    p10: normalizeNumber(quantile(sorted, 0.1)),
    p50: normalizeNumber(quantile(sorted, 0.5)),
    p90: normalizeNumber(quantile(sorted, 0.9)),
    mean: normalizeNumber(mean),
  };
}

/**
 * Run the frontier. Deterministic: same input bundle + seed → byte-identical
 * normalized result (MVP criterion 1); the result embeds the input hash
 * (criterion 11) and a scenario-only claim on every row (criterion 9).
 */
export function runFrontier(input: FrontierInput): FrontierResult {
  const notes = validateInput(input);
  const inputHash = hashInputBundle({
    seed: input.seed,
    draws: input.draws,
    horizonMonths: input.horizonMonths,
    offers: input.offers,
    menus: input.menus,
    choiceEvidence: input.choiceEvidence,
    valueEvidence: input.valueEvidence,
    exposure: input.exposure,
    baseline: input.baseline,
    candidatePolicies: input.candidatePolicies,
    valueBasis: input.valueBasis,
    modelVersion: MODEL_VERSION,
  });

  const rng = mulberry32(input.seed);
  const choiceByMenu = new Map(input.choiceEvidence.map((evidence) => [evidence.menuId, evidence]));
  const valueModels = new Map<string, OfferValueModel>(
    input.offers.map((offer) => [offer.offerId, buildOfferValueModel(input.valueEvidence[offer.offerId])]),
  );

  const drawOfferValue = (offerId: string, drawRng: Rng): number => {
    const model = valueModels.get(offerId);
    if (!model) throw new Error(`No value model for offer ${offerId}`);
    const evidence = input.valueEvidence[offerId];
    const underlying =
      evidence.kind === 'trial'
        ? (innerRng: Rng) => drawOfferValue(evidence.underlyingOfferId, innerRng)
        : undefined;
    return model.draw(drawRng, input.horizonMonths, underlying);
  };

  const policies = [input.baseline, ...input.candidatePolicies];
  const policyDraws = new Map<string, number[]>(policies.map((policy) => [policy.policyId, []]));
  const offerValueDraws = new Map<string, number[]>(input.offers.map((offer) => [offer.offerId, []]));
  const baselineDraws = policyDraws.get(input.baseline.policyId)!;

  for (let draw = 0; draw < input.draws; draw += 1) {
    // One joint posterior draw shared by every policy in this iteration —
    // policies are compared on the SAME world, which is what makes
    // probBetterThanBaseline meaningful.
    const offerValues: Record<string, number> = {};
    for (const offer of input.offers) {
      offerValues[offer.offerId] = drawOfferValue(offer.offerId, rng);
      offerValueDraws.get(offer.offerId)!.push(offerValues[offer.offerId]);
    }

    const menuValues: Record<string, number> = {};
    for (const menu of input.menus) {
      const evidence = choiceByMenu.get(menu.menuId);
      if (!evidence) {
        // Menu with no observed choice evidence: blocked from value math —
        // recorded per-policy below; value contribution treated as 0 here.
        menuValues[menu.menuId] = 0;
        continue;
      }
      const { choiceProbs } = drawMenuChoices(rng, evidence, menu.offerIds);
      menuValues[menu.menuId] = menu.offerIds.reduce(
        (sum, offerId) => sum + choiceProbs[offerId] * offerValues[offerId],
        0,
      );
    }

    for (const policy of policies) {
      policyDraws.get(policy.policyId)!.push(policyValue(policy, menuValues));
    }
  }

  const offerValueSummaries: OfferValueSummary[] = input.offers.map((offer) => {
    const model = valueModels.get(offer.offerId)!;
    return {
      offerId: offer.offerId,
      model: model.model,
      ...(model.fallbackReason ? { fallbackReason: model.fallbackReason } : {}),
      ...summarize(offerValueDraws.get(offer.offerId)!),
      claim: 'scenario' as const,
    };
  });

  const frontier: FrontierRow[] = policies.map((policy) => {
    const draws = policyDraws.get(policy.policyId)!;
    const blockedReasons: string[] = ['observational_confound'];
    for (const menuId of policyMenuIds(policy)) {
      if (!choiceByMenu.has(menuId)) blockedReasons.push(`menu_without_choice_evidence:${menuId}`);
      const menu = input.menus.find((candidate) => candidate.menuId === menuId);
      if (menu?.claim === 'scenario') blockedReasons.push(`scenario_menu:${menuId}`);
    }
    const isBaseline = policy.policyId === input.baseline.policyId;
    const wins = isBaseline
      ? null
      : draws.reduce((count, value, index) => (value > baselineDraws[index] ? count + 1 : count), 0);
    return {
      policyId: policy.policyId,
      kind: policy.kind,
      ...summarize(draws),
      probBetterThanBaseline: wins === null ? null : normalizeNumber(wins / (draws.length || 1)),
      claim: 'scenario' as const,
      blockedReasons,
    };
  });

  const coverageShare =
    input.exposure.paywallReached > 0
      ? normalizeNumber(input.exposure.exposureCovered / input.exposure.paywallReached)
      : 0;

  return {
    modelVersion: MODEL_VERSION,
    seed: input.seed,
    draws: input.draws,
    horizonMonths: input.horizonMonths,
    valueBasis: input.valueBasis,
    inputHash,
    exposure: { ...input.exposure, coverageShare },
    offerValues: offerValueSummaries,
    frontier,
    causalWinnerAllowed: false,
    notes,
  };
}
